/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import SectionData from 'components/machine/sectionData';
import React, { ReactElement } from 'react';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { SectionRowElementBadge } from 'templates/pubSub/types/TemplateType';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import { usePubSubNode, usePubSubValue } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';

interface ISectionElementContentBadgeProps {
  templateContext: TemplateContext;
  element: SectionRowElementBadge;
  cols: string;
  startingNodeId?: NodeIdentifier;
}

export default function SectionElementContentBadge(props: ISectionElementContentBadgeProps): ReactElement | null {
  const filter = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.valueVariable?.variablePath,
  );
  const node = usePubSubNode(filter, props.startingNodeId);
  const value = usePubSubValue(node);
  if (
    TemplateHelperPubSub.shouldHideOptionalVariable(
      value,
      props.element.valueVariable,
      `SectionElementContentBadge:${props.element.id}`,
    )
  ) {
    return null;
  }

  const filterLabel = TemplateHelperPubSub.createFilter(
    props.templateContext.namespaces,
    props.element.labelVariable?.variablePath,
  );
  const nodeLabel = usePubSubNode(filterLabel, props.startingNodeId);
  const valueLabel = usePubSubValue(nodeLabel);

  const text = TemplateHelperPubSub.getBadgeDisplayValue(props.element, value);
  const style = {
    backgroundColor: TemplateHelperPubSub.getBadgeBackgroundColor(props.element, value),
    color: TemplateHelperPubSub.getBadgeTextColor(props.element, value),
  };

  const label = valueLabel !== undefined ? valueLabel : props.element.label;

  return (
    <SectionData cols={props.cols} label={label}>
      <span className="umati-badge" style={style}>
        {text}
      </span>
    </SectionData>
  );
}
